import { useTranslation } from 'react-i18next'

import TextIcon from '@assets/images/form-type-text-icon.svg?react'
import DateIcon from '@assets/images/form-type-date-icon.svg?react'
import SignatureIcon from '@assets/images/form-type-signature-icon.svg?react'
import CheckboxIcon from '@assets/images/form-type-checkbox-icon.svg?react'
import RadioIcon from '@assets/images/form-type-radio-icon.svg?react'
import SelectIcon from '@assets/images/form-type-select-icon.svg?react'
import SelectEditableIcon from '@assets/images/form-type-select-editable-icon.svg?react'
import GridIcon from '@assets/images/form-type-grid-icon.svg?react'
import AddIcon from '@mui/icons-material/AddRounded'

import { Button } from '@components/Button'
import { DropDown } from '@components/DropDown'

import { FieldType } from '@types'

import {
  AddIconContainer,
  DropdownMenu,
  Item,
  ItemLabel,
  Section,
  SectionContent,
  SectionHeader,
  SectionTitle,
} from './MenuFormArrayModels.styled'

type TranslationKey =
  | 'modules_project-form-models_components_MenuFormModels_type_radio'
  | 'modules_project-form-models_components_MenuFormModels_type_checkbox'
  | 'modules_project-form-models_components_MenuFormModels_type_select'
  | 'modules_project-form-models_components_MenuFormModels_type_select_editable'
  | 'modules_project-form-models_components_MenuFormModels_type_text'
  | 'modules_project-form-models_components_MenuFormModels_type_date'
  | 'modules_project-form-models_components_MenuFormModels_type_signature'
  | 'modules_project-form-models_components_MenuFormModels_type_text_grid'

interface FieldOption {
  type: FormFieldType
  Icon: React.FC
  translationKey: TranslationKey
}

export type FormFieldType = FieldType

interface MenuFormModelsProps {
  onSelect: (type: FormFieldType) => void
}

const FIELD_OPTIONS: FieldOption[] = [
  {
    type: FieldType.Text,
    Icon: TextIcon,
    translationKey: 'modules_project-form-models_components_MenuFormModels_type_text',
  },
  {
    type: FieldType.Radio,
    Icon: RadioIcon,
    translationKey: 'modules_project-form-models_components_MenuFormModels_type_radio',
  },
  {
    type: FieldType.Checkbox,
    Icon: CheckboxIcon,
    translationKey: 'modules_project-form-models_components_MenuFormModels_type_checkbox',
  },
  {
    type: FieldType.Select,
    Icon: SelectIcon,
    translationKey: 'modules_project-form-models_components_MenuFormModels_type_select',
  },
  {
    type: FieldType.SelectEditable,
    Icon: SelectEditableIcon,
    translationKey: 'modules_project-form-models_components_MenuFormModels_type_select_editable',
  },
  {
    type: FieldType.GridText,
    Icon: GridIcon,
    translationKey: 'modules_project-form-models_components_MenuFormModels_type_text_grid',
  },
  {
    type: FieldType.Date,
    Icon: DateIcon,
    translationKey: 'modules_project-form-models_components_MenuFormModels_type_date',
  },
  {
    type: FieldType.Signature,
    Icon: SignatureIcon,
    translationKey: 'modules_project-form-models_components_MenuFormModels_type_signature',
  },
]

export const MenuFormArrayModels: React.FC<MenuFormModelsProps> = ({ onSelect }: MenuFormModelsProps) => {
  const { t } = useTranslation()

  const handleMenuItemClick = (fieldType: FormFieldType, hide: () => void) => {
    onSelect(fieldType)
    hide()
  }

  return (
    <DropDown
      WrappedComponent={() => (
        <Button size="medium" variant="faded" type="button">
          <AddIconContainer>
            <AddIcon />
          </AddIconContainer>
          {t('modules_project-form-models_components_MenuFormModels_add')}
        </Button>
      )}
      WrappedChildren={({ hide }) => (
        <DropdownMenu onMouseMove={(event: React.MouseEvent) => event.stopPropagation()}>
          <MenuFormModelSection items={FIELD_OPTIONS} onClick={(fieldType) => handleMenuItemClick(fieldType, hide)} />
        </DropdownMenu>
      )}
    />
  )
}

interface MenuFormModelSectionProps {
  items: FieldOption[]
  onClick: (fieldType: FormFieldType) => void
}

const MenuFormModelSection: React.FC<MenuFormModelSectionProps> = ({ items, onClick }: MenuFormModelSectionProps) => {
  const { t } = useTranslation()

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{t('modules_project-form-models_components_MenuFormModels_title_to_complete')}</SectionTitle>
      </SectionHeader>
      <SectionContent>
        {items.map((item, index) => (
          <Item key={index} onClick={() => onClick(item.type)}>
            {<item.Icon />}
            <ItemLabel>{t(item.translationKey)}</ItemLabel>
          </Item>
        ))}
      </SectionContent>
    </Section>
  )
}
